import { useDepartmentText } from 'model/DepartmentTerminology';
import { Box, Divider, Loader, SimpleGrid, Stack } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import { PriceOfferGetResponse } from 'api/actions/price-offer-get/price-offer-get-response';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import RevisionStatusIcon from 'components/RevisionStatusIcon';
import FaultSeverityLabel from 'components/device/FaultSeverityLabel';
import FilePreview from 'components/files/FilePreview';
import FileThumbnail from 'components/files/FileThumbnail';
import DeviceFiles from 'components/forms/device/DeviceFiles';
import { DeviceFile } from 'components/forms/device/DeviceForm';
import FormInputGroup from 'components/forms/FormInputGroup';
import Slider from 'components/modals/slider/Slider';
import SimpleValueFormatter from 'components/tables/SimpleValueFormatter';
import MeasurementContextDisplay from 'components/tables/data/fault/MeasurementContextDisplay';
import PriceOfferActions from 'components/tables/data/price-offer/PriceOfferActions';
import PriceOfferStatusIcon from 'components/tables/data/price-offer/PriceOfferStatusIcon';
import H3Medium from 'components/typography/H3Medium';
import P1Medium from 'components/typography/P1Medium';
import P2Regular from 'components/typography/P2Regular';
import ReadonlyText from 'components/typography/ReadonlyText';
import panic from 'errors/panic';
import useOrganizationModules from 'features/organization-modules/use-organization-modules';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { DeviceFileType } from 'model/DeviceFileType';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FORM_PAGE_PATH, PRICE_OFFERS_PAGE_PATH, REVISION_PAGE_PATH } from 'routes/paths';
import { formatDateTime, formatPerson, formatPrice } from 'pages/price-offers-module/price-offer-format';
import P2Medium from 'components/typography/P2Medium';
import { getEvidenceFieldValue } from 'utils/evidence-field';

type PriceOfferDevice = PriceOfferGetResponse['device'];
type PriceOfferDeviceFault = NonNullable<PriceOfferDevice['faults']>[number];
type PriceOfferDeviceEvidenceField = NonNullable<PriceOfferDevice['evidenceFields']>[number];
type PriceOfferRevisionDocument = PriceOfferGetResponse['revisionDocuments'][number];

/**
 * Labels a revision document kind for the price-offer detail card.
 */
function getRevisionDocumentKindLabel(kind: string): string {
  return kind === 'revision' ? 'Revízna správa' : 'Formulár';
}

/**
 * Resolves the direct detail path for an associated revision document.
 */
function getRevisionDocumentDetailPath(document: PriceOfferRevisionDocument): string {
  return document.kind === 'revision'
    ? REVISION_PAGE_PATH.insert({ revisionId: String(document.revisionId) })
    : FORM_PAGE_PATH.insert({ revisionId: String(document.revisionId) });
}

/**
 * Displays the readable revision reports and forms associated with a price offer device.
 */
function PriceOfferRevisionDocuments({
  documents,
  isMobile,
}: {
  documents: PriceOfferRevisionDocument[];
  isMobile: boolean;
}) {
  const navigate = useNavigate();
  const { revisions, forms } = useOrganizationModules();

  if (!revisions && !forms) {
    return null;
  }

  const showKind = revisions && forms;
  const title = showKind ? 'Revízie a formuláre' : revisions ? 'Revízie' : 'Formuláre';
  const emptyMessage = showKind
    ? 'K zariadeniu nie sú dostupné žiadne revízie ani formuláre.'
    : revisions
    ? 'K zariadeniu nie sú dostupné žiadne revízie.'
    : 'K zariadeniu nie sú dostupné žiadne formuláre.';
  const enabledDocuments = documents.filter((document) => (document.kind === 'revision' ? revisions : forms));

  return (
    <FormInputGroup
      groupTitle={title}
      stackProps={{ bg: 'white', pb: 40, pr: 40, style: { borderRadius: '4px' } }}
      withoutLeftBorder
    >
      {enabledDocuments.length === 0 ? (
        <P2Regular color="gray.7">{emptyMessage}</P2Regular>
      ) : (
        <Stack spacing={0}>
          {!isMobile && (
            <SimpleGrid cols={showKind ? 4 : 3} spacing={16} p={16} bg="gray.0">
              {showKind && <P1Medium>Typ</P1Medium>}
              <P1Medium>Názov</P1Medium>
              <P1Medium>Stav</P1Medium>
              <P1Medium>Akcie</P1Medium>
            </SimpleGrid>
          )}
          {enabledDocuments.map((document) => (
            <SimpleGrid
              key={document.revisionId}
              cols={isMobile ? 1 : showKind ? 4 : 3}
              spacing={16}
              py={16}
              px={16}
              sx={(theme) => ({ borderBottom: `1px solid ${theme.colors.gray[1]}`, alignItems: 'center' })}
            >
              {showKind &&
                (isMobile ? (
                  <ReadonlyText title="Typ" value={getRevisionDocumentKindLabel(document.kind)} />
                ) : (
                  <P2Medium>{getRevisionDocumentKindLabel(document.kind)}</P2Medium>
                ))}
              {isMobile ? (
                <ReadonlyText title="Názov" value={document.revisionName} />
              ) : (
                <P2Regular>{document.revisionName}</P2Regular>
              )}
              {isMobile ? (
                <ReadonlyText
                  title="Stav"
                  value={
                    <RevisionStatusIcon statusId={document.revisionStatus.revisionStatusId} kind={document.kind} />
                  }
                />
              ) : (
                <RevisionStatusIcon statusId={document.revisionStatus.revisionStatusId} kind={document.kind} />
              )}
              <Box>
                <Button
                  compact
                  leftIcon={<IconEye size={16} />}
                  onClick={() => navigate(getRevisionDocumentDetailPath(document))}
                  aria-label={`Zobraziť ${document.revisionName}`}
                >
                  Zobraziť
                </Button>
              </Box>
            </SimpleGrid>
          ))}
        </Stack>
      )}
    </FormInputGroup>
  );
}

/**
 * Displays price offer status changes in the same table-like style as revision plan changes.
 */
function PriceOfferChangeLog({ priceOffer }: { priceOffer: PriceOfferGetResponse }) {
  const { isMobile, isTablet } = useResponsiveDesign();
  const gridStyle = useMemo(
    () => ({
      borderBottom: '1px solid var(--mantine-color-gray-1)',
      alignItems: 'flex-start',
    }),
    []
  );

  return (
    <Stack pt={24} pb={40} px={isTablet ? 20 : 40} bg="white" style={{ borderRadius: '4px' }} spacing={24}>
      <H3Medium color="gray.7">Záznamy</H3Medium>
      {isMobile ? (
        <Stack spacing={0}>
          {priceOffer.logs.map((log) => (
            <Stack
              key={log.priceOfferChangeLogId}
              spacing={12}
              py={16}
              px={16}
              sx={(theme) => ({ borderBottom: `1px solid ${theme.colors.gray[1]}` })}
            >
              <ReadonlyText title="Nový stav" value={<PriceOfferStatusIcon status={log.newStatus} />} />
              <ReadonlyText title="Používateľ" value={formatPerson(log.user)} />
              <ReadonlyText title="Dátum" value={formatDateTime(log.timestamp)} />
              <ReadonlyText title="Poznámka" value={log.note ?? ''} />
            </Stack>
          ))}
        </Stack>
      ) : (
        <Stack spacing={0}>
          <SimpleGrid spacing={24} p={16} cols={4} bg="gray.0">
            <P1Medium>Nový stav</P1Medium>
            <P1Medium>Používateľ</P1Medium>
            <P1Medium>Dátum</P1Medium>
            <P1Medium>Poznámka</P1Medium>
          </SimpleGrid>
          {priceOffer.logs.map((log) => (
            <SimpleGrid key={log.priceOfferChangeLogId} spacing={24} py={24} px={16} style={gridStyle} cols={4}>
              <PriceOfferStatusIcon status={log.newStatus} />
              <P2Regular>{formatPerson(log.user)}</P2Regular>
              <P2Regular>{formatDateTime(log.timestamp)}</P2Regular>
              {log.note ? (
                <P2Regular>{log.note}</P2Regular>
              ) : (
                <Box data-testid={`empty-price-offer-log-${log.priceOfferChangeLogId}-note`} />
              )}
            </SimpleGrid>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Maps readonly device files into the DeviceFiles component shape.
 */
function mapDeviceFiles(device: PriceOfferDevice): DeviceFile[] {
  return (device.files ?? []).map((file) => ({
    deviceFileId: file.deviceFileId,
    fileName: file.fileName,
    fileId: file.fileId,
    key: String(file.deviceFileId ?? file.fileId),
    uploadedAt: file.created ? new Date(file.created) : new Date(),
    originalFileName: file.fileName,
    deleted: false,
    isMainImage: file.isMainImage,
    hasKepSignature: file.hasKepSignature,
    type: file.fileType as DeviceFileType,
  }));
}

/**
 * Formats a device evidence field value for readonly detail display.
 */
function formatEvidenceFieldValue(field: PriceOfferDeviceEvidenceField): string | JSX.Element {
  const value = getEvidenceFieldValue(field);
  return value === undefined || value === null || value === '' ? (
    '-'
  ) : (
    <SimpleValueFormatter value={value} precision={field.fieldType === 'number' ? field.precision : undefined} />
  );
}

/**
 * Displays a compact fault attachment preview and opens all fault files in the slider.
 */
function PriceOfferFaultFiles({ fault }: { fault: PriceOfferDeviceFault }) {
  const [openedSlider, setOpenSlider] = useState(false);
  const files = fault.files ?? [];
  const firstFile = files[0] ?? null;
  const fileIds = useMemo(() => files.map((file) => file.fileId), [files]);

  const handleOpenSlider = useCallback(() => {
    setOpenSlider(true);
  }, []);

  if (!firstFile) {
    return <Box data-testid={`empty-price-offer-fault-${fault.faultId}-files`} />;
  }

  return (
    <Box w={52} h={52} miw={52} mih={52} maw={52} mah={52}>
      <FileThumbnail fileId={firstFile.fileId} onOpenSlider={handleOpenSlider} />
      <Slider
        key={firstFile.fileId}
        opened={openedSlider}
        fileIds={fileIds}
        onClose={() => setOpenSlider(false)}
        initialImageId={firstFile.fileId}
      />
    </Box>
  );
}

/**
 * Displays readonly faults for a price offer device.
 */
function PriceOfferDeviceFaults({ faults }: { faults: PriceOfferDeviceFault[] }) {
  const { isTablet } = useResponsiveDesign();
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: isTablet ? '1fr' : '200px minmax(140px, 1fr) minmax(180px, 1.5fr) 170px 170px 72px',
    gap: isTablet ? '8px' : '16px',
    alignItems: 'flex-start',
  };

  return (
    <Stack pt={24} pb={40} px={isTablet ? 20 : 40} bg="white" style={{ borderRadius: '4px' }} spacing={24}>
      <H3Medium color="gray.7">Nedostatky zariadenia</H3Medium>
      {faults.length === 0 ? (
        <P2Regular color="gray.7">Zariadenie nemá žiadne nedostatky</P2Regular>
      ) : (
        <Stack spacing={0}>
          <Box
            py={12}
            px={16}
            bg="gray.0"
            sx={(theme) => ({ border: `1px solid ${theme.white}`, borderRadius: '4px 4px 0 0' })}
            style={gridStyle}
          >
            <P1Medium color="gray.8">Závažnosť</P1Medium>
            <P1Medium color="gray.8">Názov</P1Medium>
            <P1Medium color="gray.8">Popis</P1Medium>
            <P1Medium color="gray.8">Meranie</P1Medium>
            <P1Medium color="gray.8">Dodatok</P1Medium>
            <P1Medium color="gray.8">Súbory</P1Medium>
          </Box>
          {faults.map((fault) => (
            <Box
              py={12}
              px={16}
              key={fault.faultId}
              sx={(theme) => ({
                borderBottom: `1px solid ${theme.colors.gray[1]}`,
                borderLeft: `1px solid ${theme.white}`,
                borderRight: `1px solid ${theme.white}`,
              })}
              style={gridStyle}
            >
              <Box>
                {fault.faultSeverity ? (
                  <FaultSeverityLabel
                    faultSeverityId={fault.faultSeverity.faultSeverityId}
                    faultSeverityName={fault.faultSeverity.faultSeverityName}
                    isFaultFixed={false}
                  />
                ) : (
                  <Box data-testid={`empty-price-offer-fault-${fault.faultId}-severity`} />
                )}
              </Box>
              <P2Medium color="gray.8">{fault.faultName}</P2Medium>
              {fault.description ? (
                <P2Regular color="gray.8">{fault.description}</P2Regular>
              ) : (
                <Box data-testid={`empty-price-offer-fault-${fault.faultId}-description`} />
              )}
              <Box>
                {fault.measurementContext ? (
                  <MeasurementContextDisplay measurementContext={fault.measurementContext} />
                ) : (
                  <Box data-testid={`empty-price-offer-fault-${fault.faultId}-measurement`} />
                )}
              </Box>
              {fault.moreInfo ? (
                <P2Regular color="gray.8">{fault.moreInfo}</P2Regular>
              ) : (
                <Box data-testid={`empty-price-offer-fault-${fault.faultId}-more-info`} />
              )}
              <PriceOfferFaultFiles fault={fault} />
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Page with readonly details of a price offer.
 */
export default function PriceOfferPage() {
  const departmentText = useDepartmentText();
  const { getAction } = useApi();
  const { priceOfferId } = useParams();
  const { isMobile, isTablet } = useResponsiveDesign();
  const [priceOffer, setPriceOffer] = useState<PriceOfferGetResponse | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [priceOfferFileSliderOpened, setPriceOfferFileSliderOpened] = useState(false);
  const deviceFiles = useMemo(() => (priceOffer ? mapDeviceFiles(priceOffer.device) : []), [priceOffer]);

  useEffect(() => {
    if (!priceOfferId) {
      return;
    }

    const action = getAction('PriceOfferGet');
    action({ parameters: { priceOfferId } }).then(setPriceOffer).catch(panic);
  }, [getAction, priceOfferId, refreshToken]);

  const pageTitle = priceOffer?.device.deviceName
    ? `Cenová ponuka na zariadenie "${priceOffer.device.deviceName}"`
    : 'Cenová ponuka';

  return (
    <DashboardLayout
      title={pageTitle}
      breadcrumbs={[{ title: 'Cenové ponuky', link: PRICE_OFFERS_PAGE_PATH.original }, { title: pageTitle }]}
      actions={
        priceOffer && (
          <PriceOfferActions
            priceOffer={priceOffer}
            compactOnTablet={isMobile}
            onChanged={() => setRefreshToken((value) => value + 1)}
          />
        )
      }
    >
      {!priceOffer ? (
        <Stack align="center" pt={80}>
          <Loader />
        </Stack>
      ) : (
        <Stack spacing={24}>
          <FormInputGroup
            groupTitle="Základné informácie"
            stackProps={{ bg: 'white', pb: 40, pr: 40, style: { borderRadius: '4px' } }}
          >
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Stav" value={<PriceOfferStatusIcon status={priceOffer.status} />} />
              <ReadonlyText title="Vytvorená" value={formatDateTime(priceOffer.createdAt)} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Zadávateľ" value={formatPerson(priceOffer.createdBy)} />
              <ReadonlyText title="Vypracovateľ" value={formatPerson(priceOffer.assignedTo)} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Popis" value={priceOffer.description ?? '-'} />
              <ReadonlyText title="Cena práce" value={formatPrice(priceOffer.finalPriceLabor)} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Cena materiálu" value={formatPrice(priceOffer.finalPriceMaterial)} />
              <ReadonlyText title="Cena spolu" value={formatPrice(priceOffer.finalPrice)} />
            </SimpleGrid>
          </FormInputGroup>

          {priceOffer.fileId && (
            <FormInputGroup
              groupTitle="Cenová ponuka"
              stackProps={{ bg: 'white', pb: 40, pr: 40, style: { borderRadius: '4px' } }}
              withoutLeftBorder
            >
              <FilePreview
                fileId={priceOffer.fileId}
                hideDelete
                onOpenSlider={() => setPriceOfferFileSliderOpened(true)}
              />
              <Slider
                opened={priceOfferFileSliderOpened}
                fileIds={[priceOffer.fileId]}
                onClose={() => setPriceOfferFileSliderOpened(false)}
                initialImageId={priceOffer.fileId}
              />
            </FormInputGroup>
          )}

          <PriceOfferChangeLog priceOffer={priceOffer} />

          <FormInputGroup
            groupTitle="Zariadenie"
            stackProps={{ bg: 'white', pb: 40, pr: 40, style: { borderRadius: '4px' } }}
          >
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Názov" value={priceOffer.device.deviceName} />
              <ReadonlyText title="Zariadenie" value={priceOffer.device.deviceType.deviceTypeName} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Typ zariadenia" value={priceOffer.device.deviceSubtype.deviceTypeName} />
              <ReadonlyText title="Organizácia" value={priceOffer.organization.organizationName} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText
                title={departmentText('Položka úrovne', 'Stredisko')}
                value={priceOffer.department.departmentName}
              />
              <ReadonlyText title="Budova" value={priceOffer.device.building ?? '-'} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Poschodie" value={priceOffer.device.floor ?? '-'} />
              <ReadonlyText title="Miestnosť" value={priceOffer.device.room ?? '-'} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Výrobca" value={priceOffer.device.manufacturer ?? '-'} />
              <ReadonlyText title="Sériové číslo" value={priceOffer.device.serialNumber ?? '-'} />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText
                title="Rok výroby"
                value={priceOffer.device.manufactured ? String(priceOffer.device.manufactured) : '-'}
              />
            </SimpleGrid>
            {(priceOffer.device.evidenceFields ?? []).length > 0 && (
              <>
                <Divider color="gray.2" />
                <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                  {priceOffer.device.evidenceFields!.map((field) => (
                    <ReadonlyText
                      key={field.deviceEvidenceFieldId ?? field.evidenceFieldId}
                      title={field.fieldName ?? 'Evidenčné pole'}
                      value={formatEvidenceFieldValue(field)}
                    />
                  ))}
                </SimpleGrid>
              </>
            )}
          </FormInputGroup>

          <PriceOfferRevisionDocuments documents={priceOffer.revisionDocuments} isMobile={isMobile} />

          <FormInputGroup
            groupTitle="Súbory zariadenia"
            stackProps={{ bg: 'white', pb: 40, pr: 40, style: { borderRadius: '4px' } }}
            withoutLeftBorder
          >
            <DeviceFiles
              context="price-offer-detail"
              deviceId={priceOffer.device.deviceId}
              deviceName={priceOffer.device.deviceName}
              departmentId={priceOffer.department.departmentId}
              files={deviceFiles}
              readonly
            />
          </FormInputGroup>

          <PriceOfferDeviceFaults faults={priceOffer.device.faults ?? []} />
        </Stack>
      )}
    </DashboardLayout>
  );
}
